import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import {
  Box, Typography, Chip, IconButton, Select, MenuItem,
  FormControl, Tooltip, CircularProgress, Drawer,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import ScheduleIcon from '@material-ui/icons/Schedule';
import SpeedIcon from '@material-ui/icons/Speed';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import DateRangeIcon from '@material-ui/icons/DateRange';
import GetAppIcon from '@material-ui/icons/GetApp';
import RoomIcon from '@material-ui/icons/Room';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import HomeIcon from '@material-ui/icons/Home';
import { GoogleMap, useJsApiLoader, Marker, Polyline } from '@react-google-maps/api';
import { AuthContext } from '../../App';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const LIBRARIES = ['places'];

const useStyles = makeStyles(() => ({
  root: {
    flex: 1, minHeight: 0,
    display: 'flex', flexDirection: 'column',
    background: '#fafbfc',
  },
  // Single-row compact toolbar
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 20px',
    background: '#fff',
    borderBottom: '1px solid #e6eaf0',
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '0.88rem', fontWeight: 800, color: '#0b1220', letterSpacing: '-0.01em',
  },
  // Inline KPIs ribbon — single horizontal strip, no separate cards
  kpisInline: {
    display: 'flex', alignItems: 'center', gap: 16, marginLeft: 'auto',
    fontSize: '0.74rem', color: '#4a5567', fontWeight: 600,
  },
  kpiInline: { display: 'flex', alignItems: 'center', gap: 5 },
  kpiInlineDot: {
    width: 6, height: 6, borderRadius: 3,
  },
  filterChip: {
    display: 'flex', alignItems: 'center', gap: 5,
    background: '#f4f6f9', borderRadius: 8,
    padding: '5px 10px',
    border: '1px solid transparent',
    transition: 'border-color 0.12s',
    '&:focus-within': { borderColor: '#2563eb' },
  },
  filterSelect: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8, fontSize: '0.74rem',
      background: '#fff', height: 32,
    },
    '& .MuiOutlinedInput-input': { padding: '6px 26px 6px 10px' },
  },

  // Main content area
  body: { flex: 1, minHeight: 0, overflowY: 'auto' },
  scroll: { padding: '8px 20px 20px' },

  // Group section
  groupHeader: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '14px 0 6px',
    fontSize: '0.66rem', fontWeight: 800, color: '#4a5567',
    textTransform: 'uppercase', letterSpacing: '0.08em',
    cursor: 'pointer',
    userSelect: 'none',
  },
  groupCount: { fontSize: '0.6rem', color: '#8b95a6', fontWeight: 600 },
  groupBar: { flex: 1, height: 1, background: '#e6eaf0' },

  // Compact table
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    background: '#fff',
    border: '1px solid #e6eaf0',
    borderRadius: 10,
    overflow: 'hidden',
  },
  thead: {
    background: 'linear-gradient(180deg, #f8fafc, #f1f5f9)',
  },
  th: {
    textAlign: 'left',
    padding: '8px 12px',
    fontSize: '0.6rem', fontWeight: 800, color: '#4a5567',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    borderBottom: '1px solid #e6eaf0',
    whiteSpace: 'nowrap',
  },
  tr: {
    cursor: 'pointer',
    transition: 'background 0.1s',
    '&:hover': { background: '#f8fafc' },
    '&:not(:last-child) td': { borderBottom: '1px solid #eef1f5' },
  },
  trActive: { background: '#eff6ff !important' },
  td: {
    padding: '7px 12px',
    fontSize: '0.74rem', color: '#0b1220',
    height: 36,
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  tdMuted: { color: '#4a5567', fontWeight: 500 },
  tdStrong: { fontWeight: 700 },
  tdEllipsis: {
    maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },

  pillType: {
    display: 'inline-block',
    padding: '2px 8px', borderRadius: 999,
    fontSize: '0.62rem', fontWeight: 700,
  },
  pillStatus: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 8px', borderRadius: 999,
    fontSize: '0.6rem', fontWeight: 700,
  },

  // Detail drawer
  drawer: {
    '& .MuiDrawer-paper': {
      width: 480, maxWidth: '95vw',
      background: '#fff',
    },
  },
  drawerHeader: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    padding: '14px 18px',
    borderBottom: '1px solid #eef1f5',
    background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
  },
  drawerBody: {
    padding: '14px 18px',
    overflowY: 'auto',
    flex: 1,
  },
  detailMap: {
    width: '100%', height: 200,
    borderRadius: 12, overflow: 'hidden',
    border: '1px solid #e6eaf0',
    marginBottom: 14,
    background: '#f4f6f9',
  },
  section: { marginBottom: 14 },
  sectionTitle: {
    fontSize: '0.6rem', fontWeight: 800, color: '#8b95a6',
    textTransform: 'uppercase', letterSpacing: '0.08em',
    marginBottom: 6,
  },
  detailGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
  },
  detailCell: {
    background: '#f4f6f9', borderRadius: 10, padding: '8px 10px',
  },
  detailCellLabel: { fontSize: '0.56rem', fontWeight: 700, color: '#8b95a6', textTransform: 'uppercase', letterSpacing: '0.05em' },
  detailCellValue: { fontSize: '0.78rem', fontWeight: 700, color: '#0b1220', marginTop: 2 },

  empty: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: 40, gap: 8,
  },
  emptyIcon: {
    width: 64, height: 64, borderRadius: 32,
    background: '#f4f6f9',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#8b95a6',
  },
}));

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit' });
}
function fmtDateTime(d) {
  if (!d) return null;
  return new Date(d).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function fmtTime(d) {
  if (!d) return null;
  return new Date(d).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
}
function fmtDur(min) {
  if (min == null) return '—';
  const m = Math.round(min);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
}

const SERVICE_TYPE_LABELS = {
  delivery: 'Entrega',
  assistance: 'Assistência',
  fuel: 'Combustível',
};
const SERVICE_TYPE_STYLE = {
  delivery:   { bg: '#dbeafe', fg: '#1d4ed8' },
  assistance: { bg: '#ede9fe', fg: '#6d28d9' },
  fuel:       { bg: '#fef3c7', fg: '#b45309' },
};

// ISO week number — used for "Group by week".
function isoWeekKey(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

// Resolve the "previous stop" of an OT inside its consolidated route — the
// REAL origin point for the leg the driver actually drove. For a single-OT
// route or the first OT of a consolidated route, the start is the driver's
// home. For OTs 2+ in a consolidated route, the start is the delivery point
// of the previous OT in delivery order.
function resolveOriginContext(ot, allOrders) {
  if (!ot?.assignedRouteId) {
    return {
      kind: 'standalone',
      label: 'Origem da rota',
      name: ot?.pickupName || ot?.pickupAddress || '—',
      address: ot?.pickupAddress || null,
    };
  }
  const siblings = allOrders.filter(o =>
    o.assignedRouteId === ot.assignedRouteId
  );
  if (siblings.length <= 1) {
    return {
      kind: 'route-start',
      label: 'Início da rota',
      name: ot.pickupName || ot.pickupAddress || '—',
      address: ot.pickupAddress || null,
    };
  }
  const sorted = [...siblings].sort((a, b) => {
    const ta = a.deliveredAt ? new Date(a.deliveredAt).getTime() :
               a.deliveryWindowFrom ? new Date(a.deliveryWindowFrom).getTime() : 0;
    const tb = b.deliveredAt ? new Date(b.deliveredAt).getTime() :
               b.deliveryWindowFrom ? new Date(b.deliveryWindowFrom).getTime() : 0;
    return ta - tb;
  });
  const idx = sorted.findIndex(s => Number(s.id || s._id) === Number(ot.id || ot._id));
  if (idx <= 0) {
    return {
      kind: 'route-start',
      label: 'Início da rota · casa do motorista',
      name: ot.pickupName || ot.pickupAddress || '—',
      address: ot.pickupAddress || null,
      routeOrder: 1,
      routeTotal: sorted.length,
    };
  }
  const prev = sorted[idx - 1];
  return {
    kind: 'previous-stop',
    label: `Continuação após ${prev.orderNumber || `OT-${prev.id}`}`,
    name: prev.deliveryName || prev.deliveryAddress || '—',
    address: prev.deliveryAddress || null,
    routeOrder: idx + 1,
    routeTotal: sorted.length,
  };
}

export default function OrdersDeliveredSummary({ lang = 'pt' }) {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOTId, setSelectedOTId] = useState(null);
  const [detailRoute, setDetailRoute] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  // Filters
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const thirtyDaysAgo = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);
  const [from, setFrom] = useState(thirtyDaysAgo);
  const [to, setTo] = useState(today);
  const [driverFilter, setDriverFilter] = useState('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState('day'); // day | week | month | none

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_KEY,
    libraries: LIBRARIES,
    language: 'pt',
    region: 'PT',
  });

  const fetchOrders = useCallback(() => {
    if (!token) return;
    setLoading(true);
    const params = new URLSearchParams({ page: '1', limit: '500', status: 'delivered' });
    fetch(`/planner-api/transport-orders?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => {
        const list = Array.isArray(d?.data) ? d.data : (Array.isArray(d) ? d : []);
        setOrders(list);
      })
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Lazy-load route detail when a row is selected
  useEffect(() => {
    if (!selectedOTId) { setDetailRoute(null); return; }
    const ot = orders.find(o => Number(o.id || o._id) === Number(selectedOTId));
    if (!ot?.assignedRouteId || !token) { setDetailRoute(null); return; }
    setDetailLoading(true);
    fetch(`/planner-api/routes/${ot.assignedRouteId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(setDetailRoute)
      .catch(() => setDetailRoute(null))
      .finally(() => setDetailLoading(false));
  }, [selectedOTId, orders, token]);

  // Apply client-side filters
  const filtered = useMemo(() => {
    const fromMs = from ? new Date(from + 'T00:00:00').getTime() : 0;
    const toMs = to ? new Date(to + 'T23:59:59').getTime() : Infinity;
    const q = (search || '').trim().toLowerCase();
    return orders.filter(o => {
      const refDate = o.deliveredAt || o.serviceDate || o.actualStartAt || o.updatedAt || o.createdAt;
      const ts = refDate ? new Date(refDate).getTime() : 0;
      if (ts < fromMs || ts > toMs) return false;
      if (driverFilter !== 'all' && String(o.driverId || '') !== String(driverFilter)) return false;
      if (serviceTypeFilter !== 'all' && (o.serviceType || 'delivery') !== serviceTypeFilter) return false;
      if (q) {
        const hay = [o.orderNumber, o.deliveryName, o.deliveryAddress, o.driverName, o.vehiclePlate]
          .map(v => (v || '').toLowerCase()).join(' ');
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [orders, from, to, driverFilter, serviceTypeFilter, search]);

  // KPIs
  const kpis = useMemo(() => {
    const totalKm = filtered.reduce((acc, o) => acc + (Number(o.distanceKm) || 0), 0);
    const totalMin = filtered.reduce((acc, o) => acc + (Number(o.drivingTimeMin) || 0), 0);
    let onTime = 0, withSla = 0;
    for (const o of filtered) {
      if (!o.deliveryWindowTo || !o.deliveredAt) continue;
      withSla++;
      if (new Date(o.deliveredAt).getTime() <= new Date(o.deliveryWindowTo).getTime()) onTime++;
    }
    return {
      total: filtered.length,
      totalKm,
      totalMin,
      onTimeRate: withSla > 0 ? Math.round((onTime / withSla) * 100) : null,
    };
  }, [filtered]);

  // Group OTs based on groupBy mode
  const groups = useMemo(() => {
    const sorted = [...filtered].sort((a, b) => {
      const ta = new Date(a.deliveredAt || a.serviceDate || 0).getTime();
      const tb = new Date(b.deliveredAt || b.serviceDate || 0).getTime();
      return tb - ta;
    });
    if (groupBy === 'none') {
      return [{ key: 'all', label: 'Todas as entregas', items: sorted }];
    }
    const map = new Map();
    for (const o of sorted) {
      const ref = o.deliveredAt || o.serviceDate || o.createdAt;
      if (!ref) continue;
      const d = new Date(ref);
      let key, label;
      if (groupBy === 'day') {
        key = d.toISOString().slice(0, 10);
        label = d.toLocaleDateString('pt-PT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
      } else if (groupBy === 'week') {
        key = isoWeekKey(d);
        // Compute monday/sunday of that week for the display label
        const dow = (d.getDay() + 6) % 7; // 0=Mon
        const mon = new Date(d); mon.setDate(d.getDate() - dow);
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        label = `Semana ${key.slice(-2)} · ${mon.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })} → ${sun.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })}`;
      } else if (groupBy === 'month') {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        label = d.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
      }
      if (!map.has(key)) map.set(key, { key, label, items: [] });
      map.get(key).items.push(o);
    }
    return [...map.values()];
  }, [filtered, groupBy]);

  // Driver options
  const drivers = useMemo(() => {
    const uniq = new Map();
    for (const o of orders) {
      if (o.driverId && o.driverName) uniq.set(o.driverId, o.driverName);
    }
    return [...uniq.entries()].sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));
  }, [orders]);

  function exportCsv() {
    const cols = ['orderNumber', 'serviceType', 'serviceDate', 'driverName', 'vehiclePlate',
      'deliveryName', 'deliveryAddress', 'actualStartAt', 'deliveredAt', 'distanceKm', 'drivingTimeMin'];
    const header = cols.join(',');
    const rows = filtered.map(o => cols.map(c => {
      const v = o[c] != null ? String(o[c]).replace(/"/g, '""') : '';
      return /[,\n"]/.test(v) ? `"${v}"` : v;
    }).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `entregas_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function toggleGroup(key) {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const selected = orders.find(o => Number(o.id || o._id) === Number(selectedOTId));
  const originCtx = selected ? resolveOriginContext(selected, orders) : null;

  return (
    <Box className={classes.root}>
      {/* Compact toolbar — single row with all filters */}
      <Box className={classes.toolbar}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Box style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircleIcon style={{ fontSize: 16, color: '#16a34a' }} />
          </Box>
          <Typography className={classes.title}>Resumo de Entregues</Typography>
        </Box>

        <Box className={classes.filterChip}>
          <DateRangeIcon style={{ fontSize: 14, color: '#8b95a6' }} />
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ border: 'none', background: 'transparent', fontSize: '0.72rem', color: '#0b1220', outline: 'none', fontFamily: 'inherit', width: 110 }} />
          <span style={{ color: '#8b95a6', fontSize: 12 }}>→</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{ border: 'none', background: 'transparent', fontSize: '0.72rem', color: '#0b1220', outline: 'none', fontFamily: 'inherit', width: 110 }} />
        </Box>

        <FormControl variant="outlined" size="small" className={classes.filterSelect}>
          <Select value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            <MenuItem value="day">Agrupar por Dia</MenuItem>
            <MenuItem value="week">Agrupar por Semana</MenuItem>
            <MenuItem value="month">Agrupar por Mês</MenuItem>
            <MenuItem value="none">Sem agrupamento</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.filterSelect} style={{ minWidth: 140 }}>
          <Select value={driverFilter} onChange={e => setDriverFilter(e.target.value)}>
            <MenuItem value="all">Todos motoristas</MenuItem>
            {drivers.map(([id, name]) => <MenuItem key={id} value={String(id)}>{name}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" className={classes.filterSelect} style={{ minWidth: 130 }}>
          <Select value={serviceTypeFilter} onChange={e => setServiceTypeFilter(e.target.value)}>
            <MenuItem value="all">Todos tipos</MenuItem>
            {Object.entries(SERVICE_TYPE_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </Select>
        </FormControl>

        <Box className={classes.filterChip} style={{ minWidth: 180 }}>
          <SearchIcon style={{ fontSize: 14, color: '#8b95a6' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="OT, cliente, motorista..."
            style={{ border: 'none', background: 'transparent', fontSize: '0.72rem', color: '#0b1220', outline: 'none', flex: 1, fontFamily: 'inherit' }} />
        </Box>

        <Tooltip title="Exportar CSV">
          <IconButton size="small" onClick={exportCsv} style={{ background: '#f4f6f9', borderRadius: 8, padding: 6 }}>
            <GetAppIcon style={{ fontSize: 14, color: '#4a5567' }} />
          </IconButton>
        </Tooltip>

        {/* Inline KPIs ribbon */}
        <Box className={classes.kpisInline}>
          <Box className={classes.kpiInline}>
            <Box className={classes.kpiInlineDot} style={{ background: '#16a34a' }} />
            <span style={{ color: '#0b1220', fontWeight: 800 }}>{kpis.total}</span>
            <span style={{ color: '#8b95a6' }}>Ordens</span>
          </Box>
          <Box className={classes.kpiInline}>
            <SpeedIcon style={{ fontSize: 14, color: '#2563eb' }} />
            <span style={{ color: '#0b1220', fontWeight: 800 }}>{kpis.totalKm.toFixed(0)}</span>
            <span style={{ color: '#8b95a6' }}>km</span>
          </Box>
          <Box className={classes.kpiInline}>
            <ScheduleIcon style={{ fontSize: 14, color: '#d97706' }} />
            <span style={{ color: '#0b1220', fontWeight: 800 }}>{fmtDur(kpis.totalMin)}</span>
          </Box>
          {kpis.onTimeRate != null && (
            <Box className={classes.kpiInline}>
              <CheckCircleIcon style={{ fontSize: 14, color: '#7c3aed' }} />
              <span style={{ color: '#0b1220', fontWeight: 800 }}>{kpis.onTimeRate}%</span>
              <span style={{ color: '#8b95a6' }}>SLA</span>
            </Box>
          )}
        </Box>
      </Box>

      {/* Body — table grouped */}
      <Box className={classes.body}>
        <Box className={classes.scroll}>
          {loading && (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <CircularProgress size={20} style={{ color: '#2563eb' }} />
            </Box>
          )}
          {!loading && filtered.length === 0 && (
            <Box className={classes.empty}>
              <Box className={classes.emptyIcon}>
                <CheckCircleIcon style={{ fontSize: 28 }} />
              </Box>
              <Typography style={{ fontSize: '0.86rem', fontWeight: 700, color: '#4a5567' }}>
                Sem entregas no período
              </Typography>
              <Typography style={{ fontSize: '0.7rem', color: '#8b95a6' }}>
                Ajusta filtros ou alarga o intervalo de datas.
              </Typography>
            </Box>
          )}
          {!loading && groups.map(g => {
            const isCollapsed = collapsedGroups.has(g.key);
            // Group-level summary (km + count)
            const gKm = g.items.reduce((acc, o) => acc + (Number(o.distanceKm) || 0), 0);
            return (
              <Box key={g.key}>
                <Box className={classes.groupHeader} onClick={() => toggleGroup(g.key)}>
                  {isCollapsed
                    ? <ChevronRightIcon style={{ fontSize: 16, color: '#8b95a6' }} />
                    : <ExpandMoreIcon style={{ fontSize: 16, color: '#8b95a6' }} />}
                  <span>{g.label}</span>
                  <Box className={classes.groupBar} />
                  <span className={classes.groupCount}>
                    {g.items.length} {g.items.length === 1 ? 'ordem' : 'ordens'} · {gKm.toFixed(0)} km
                  </span>
                </Box>
                {!isCollapsed && (
                  <table className={classes.table}>
                    <thead className={classes.thead}>
                      <tr>
                        <th className={classes.th}>Ordem</th>
                        <th className={classes.th}>Tipo</th>
                        <th className={classes.th}>Destino</th>
                        <th className={classes.th}>Motorista</th>
                        <th className={classes.th}>Início</th>
                        <th className={classes.th}>Entrega</th>
                        <th className={classes.th} style={{ textAlign: 'right' }}>Km</th>
                        <th className={classes.th} style={{ textAlign: 'right' }}>Tempo</th>
                        <th className={classes.th}>SLA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.items.map(o => {
                        const isSelected = Number(selectedOTId) === Number(o.id || o._id);
                        const sty = SERVICE_TYPE_STYLE[o.serviceType] || SERVICE_TYPE_STYLE.delivery;
                        const stype = SERVICE_TYPE_LABELS[o.serviceType] || 'Entrega';
                        const onTime = o.deliveryWindowTo && o.deliveredAt
                          ? new Date(o.deliveredAt) <= new Date(o.deliveryWindowTo)
                          : null;
                        return (
                          <tr key={o.id || o._id}
                            className={`${classes.tr} ${isSelected ? classes.trActive : ''}`}
                            onClick={() => setSelectedOTId(o.id || o._id)}
                          >
                            <td className={`${classes.td} ${classes.tdStrong}`}>{o.orderNumber || `OT-${o.id}`}</td>
                            <td className={classes.td}>
                              <span className={classes.pillType} style={{ background: sty.bg, color: sty.fg }}>{stype}</span>
                            </td>
                            <td className={`${classes.td} ${classes.tdEllipsis}`} title={o.deliveryAddress || ''}>
                              {o.deliveryName || o.deliveryAddress || '—'}
                            </td>
                            <td className={`${classes.td} ${classes.tdMuted}`}>{o.driverName || '—'}</td>
                            <td className={`${classes.td} ${classes.tdMuted}`}>{fmtTime(o.actualStartAt) || '—'}</td>
                            <td className={`${classes.td} ${classes.tdMuted}`}>{fmtTime(o.deliveredAt) || '—'}</td>
                            <td className={`${classes.td} ${classes.tdStrong}`} style={{ textAlign: 'right' }}>
                              {o.distanceKm != null ? Number(o.distanceKm).toFixed(1) : '—'}
                            </td>
                            <td className={`${classes.td} ${classes.tdMuted}`} style={{ textAlign: 'right' }}>
                              {fmtDur(o.drivingTimeMin)}
                            </td>
                            <td className={classes.td}>
                              {onTime === true && (
                                <span className={classes.pillStatus} style={{ background: '#dcfce7', color: '#16a34a' }}>
                                  <CheckCircleIcon style={{ fontSize: 11 }} /> No prazo
                                </span>
                              )}
                              {onTime === false && (
                                <span className={classes.pillStatus} style={{ background: '#fee2e2', color: '#dc2626' }}>Atraso</span>
                              )}
                              {onTime == null && <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Detail drawer (slides from right) */}
      <Drawer anchor="right" open={!!selected} onClose={() => setSelectedOTId(null)} className={classes.drawer}>
        {selected && (
          <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box className={classes.drawerHeader}>
              <Box style={{ width: 32, height: 32, borderRadius: 16, background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <CheckCircleIcon style={{ fontSize: 18, color: '#fff' }} />
              </Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Typography style={{ fontSize: '1rem', fontWeight: 800, color: '#0b1220' }}>
                  {selected.orderNumber || `OT-${selected.id}`}
                </Typography>
                <Typography style={{ fontSize: '0.72rem', color: '#4a5567', marginTop: 1 }}>
                  {selected.deliveryName || selected.deliveryAddress || '—'}
                </Typography>
                {originCtx && originCtx.routeOrder ? (
                  <Chip size="small" label={`Paragem ${originCtx.routeOrder} de ${originCtx.routeTotal} da rota conjunta`}
                    style={{ height: 18, fontSize: '0.58rem', marginTop: 4, background: '#eef2ff', color: '#4338ca', fontWeight: 700 }} />
                ) : null}
              </Box>
              <IconButton size="small" onClick={() => setSelectedOTId(null)} style={{ flexShrink: 0 }}>
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            <Box className={classes.drawerBody}>
              {/* Mini-map */}
              <Box className={classes.detailMap}>
                {detailLoading && (
                  <Box style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={16} style={{ color: '#2563eb' }} />
                  </Box>
                )}
                {!detailLoading && isLoaded && (() => {
                  const coords = detailRoute?.routeData?.computedRoute?.geometry?.coordinates || [];
                  const path = coords.map(c => ({ lat: c[1], lng: c[0] }));
                  const origin = detailRoute?.origin?.coordinates;
                  const destination = detailRoute?.destination?.coordinates;
                  const center = path[0] || (selected.deliveryLat ? { lat: Number(selected.deliveryLat), lng: Number(selected.deliveryLng) } : null);
                  if (!center) return (
                    <Box style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8b95a6', fontSize: '0.72rem' }}>
                      Sem coordenadas
                    </Box>
                  );
                  return (
                    <GoogleMap
                      mapContainerStyle={{ width: '100%', height: '100%' }}
                      center={center}
                      zoom={11}
                      options={{
                        gestureHandling: 'cooperative',
                        streetViewControl: false, mapTypeControl: false,
                        fullscreenControl: false, zoomControl: true, clickableIcons: false,
                      }}
                      onLoad={(map) => {
                        if (path.length > 1) {
                          const b = new window.google.maps.LatLngBounds();
                          path.forEach(p => b.extend(p));
                          map.fitBounds(b, 20);
                        }
                      }}
                    >
                      {path.length > 1 && (
                        <Polyline path={path} options={{ strokeColor: '#2563eb', strokeOpacity: 0.85, strokeWeight: 4 }} />
                      )}
                      {origin && (
                        <Marker position={{ lat: origin[1], lng: origin[0] }} icon={{
                          path: window.google.maps.SymbolPath.CIRCLE, scale: 6,
                          fillColor: '#16a34a', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2,
                        }} />
                      )}
                      {destination && (
                        <Marker position={{ lat: destination[1], lng: destination[0] }} icon={{
                          path: window.google.maps.SymbolPath.CIRCLE, scale: 6,
                          fillColor: '#dc2626', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2,
                        }} />
                      )}
                      {selected.deliveryLat != null && selected.deliveryLng != null && (
                        <Marker position={{ lat: Number(selected.deliveryLat), lng: Number(selected.deliveryLng) }} icon={{
                          path: window.google.maps.SymbolPath.CIRCLE, scale: 8,
                          fillColor: '#f97316', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 3,
                        }} />
                      )}
                    </GoogleMap>
                  );
                })()}
              </Box>

              {/* Métricas */}
              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>Métricas desta OT</Typography>
                <Box className={classes.detailGrid}>
                  <Box className={classes.detailCell}>
                    <Typography className={classes.detailCellLabel}>Distância</Typography>
                    <Typography className={classes.detailCellValue}>
                      {selected.distanceKm != null ? `${Number(selected.distanceKm).toFixed(1)} km` : '—'}
                    </Typography>
                  </Box>
                  <Box className={classes.detailCell}>
                    <Typography className={classes.detailCellLabel}>Tempo</Typography>
                    <Typography className={classes.detailCellValue}>{fmtDur(selected.drivingTimeMin)}</Typography>
                  </Box>
                  <Box className={classes.detailCell}>
                    <Typography className={classes.detailCellLabel}>Início real</Typography>
                    <Typography className={classes.detailCellValue}>{fmtDateTime(selected.actualStartAt) || '—'}</Typography>
                  </Box>
                  <Box className={classes.detailCell}>
                    <Typography className={classes.detailCellLabel}>Entregue em</Typography>
                    <Typography className={classes.detailCellValue}>{fmtDateTime(selected.deliveredAt) || '—'}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Localizações — origem clarificada para rotas conjuntas */}
              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>Trajecto desta OT</Typography>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {originCtx && (
                    <Box style={{ background: originCtx.kind === 'previous-stop' ? '#eff6ff' : '#f0fdf4', borderRadius: 10, padding: '8px 10px', border: `1px solid ${originCtx.kind === 'previous-stop' ? '#bfdbfe' : '#bbf7d0'}` }}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                        {originCtx.kind === 'previous-stop'
                          ? <RoomIcon style={{ fontSize: 12, color: '#1d4ed8' }} />
                          : <HomeIcon style={{ fontSize: 12, color: '#16a34a' }} />}
                        <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: originCtx.kind === 'previous-stop' ? '#1d4ed8' : '#16a34a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {originCtx.label}
                        </Typography>
                      </Box>
                      <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0b1220' }}>
                        {originCtx.name}
                      </Typography>
                      {originCtx.address && originCtx.address !== originCtx.name && (
                        <Typography style={{ fontSize: '0.66rem', color: '#4a5567', marginTop: 1 }}>{originCtx.address}</Typography>
                      )}
                      {originCtx.kind === 'previous-stop' && (
                        <Typography style={{ fontSize: '0.6rem', color: '#1d4ed8', marginTop: 4, fontStyle: 'italic' }}>
                          Esta OT continua a rota a partir do destino da paragem anterior — a rota arrancou na casa do motorista mas para esta OT específica o ponto de partida é o cliente anterior.
                        </Typography>
                      )}
                    </Box>
                  )}
                  {selected.deliveryAddress && (
                    <Box style={{ background: '#fef2f2', borderRadius: 10, padding: '8px 10px', border: '1px solid #fecaca' }}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                        <RoomIcon style={{ fontSize: 12, color: '#dc2626' }} />
                        <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Destino</Typography>
                      </Box>
                      <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0b1220' }}>{selected.deliveryName || '—'}</Typography>
                      <Typography style={{ fontSize: '0.66rem', color: '#4a5567', marginTop: 1 }}>{selected.deliveryAddress}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Serviço */}
              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>Serviço</Typography>
                <Box className={classes.detailGrid}>
                  <Box className={classes.detailCell}>
                    <Typography className={classes.detailCellLabel}>Tipo</Typography>
                    <Typography className={classes.detailCellValue}>{SERVICE_TYPE_LABELS[selected.serviceType] || 'Entrega'}</Typography>
                  </Box>
                  <Box className={classes.detailCell}>
                    <Typography className={classes.detailCellLabel}>SLA</Typography>
                    <Typography className={classes.detailCellValue}>{selected.slaHours ? `${selected.slaHours}h` : '—'}</Typography>
                  </Box>
                  <Box className={classes.detailCell}>
                    <Typography className={classes.detailCellLabel}>Janela</Typography>
                    <Typography className={classes.detailCellValue} style={{ fontSize: '0.7rem' }}>
                      {selected.deliveryWindowFrom ? fmtTime(selected.deliveryWindowFrom) : '—'}
                      {selected.deliveryWindowTo ? ` → ${fmtTime(selected.deliveryWindowTo)}` : ''}
                    </Typography>
                  </Box>
                  <Box className={classes.detailCell}>
                    <Typography className={classes.detailCellLabel}>Data serviço</Typography>
                    <Typography className={classes.detailCellValue}>{fmtDate(selected.serviceDate) || '—'}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Motorista & Veículo */}
              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>Motorista & Veículo</Typography>
                <Box className={classes.detailGrid}>
                  <Box className={classes.detailCell}>
                    <Typography className={classes.detailCellLabel}>Motorista</Typography>
                    <Typography className={classes.detailCellValue}>{selected.driverName || '—'}</Typography>
                  </Box>
                  <Box className={classes.detailCell}>
                    <Typography className={classes.detailCellLabel}>Veículo</Typography>
                    <Typography className={classes.detailCellValue}>{selected.vehiclePlate || '—'}</Typography>
                  </Box>
                </Box>
              </Box>

              {selected.notes && (
                <Box className={classes.section}>
                  <Typography className={classes.sectionTitle}>Notas</Typography>
                  <Box style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '8px 10px' }}>
                    <Typography style={{ fontSize: '0.72rem', color: '#78350f' }}>{selected.notes}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
